import { Card, CardContent, CardFooter, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Career } from "@/lib/types/career";
import { getFileUrl } from "@/lib/file.util";
import { ArrowRight, Building2, Clock } from "lucide-react";
import Link from "next/link";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface CareerCardProps {
  career: Career;
  index?: number;
  className?: string;
}

export default function CareerCard({
  career,
  index,
  className = "",
}: CareerCardProps) {
  const thumbnail = getFileUrl(career.thumbnail);
  // console.log("career", career);

  return (
    <Card
      key={index}
      className={`overflow-hidden py-0 h-full transition-all duration-300 gap-0! hover:shadow-md hover:-translate-y-1 ${className}`}
    >
      <div className="relative bg-foreground/20 rounded-t-lg aspect-video w-full flex overflow-hidden">
        <img
          src={thumbnail || "/placeholder.svg"}
          alt={career.title}
          width={500}
          height={500}
          className="object-cover w-full h-full"
        />
      </div>
      <CardContent className="flex flex-col gap-4 pt-3">
        {/* Title + duration on the same row (time aligned to the title's first line) */}
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-xl font-bold">{career.title}</CardTitle>
          <div className="flex items-center shrink-0 text-gray-700 capitalize">
            <Clock className="h-5 w-5 mr-1.5" />
            <span className="whitespace-nowrap">
              {career.duration} {career.durationType ? career.durationType?.toLowerCase() + "s" : ""}
            </span>
          </div>
        </div>

        {/* Institutions */}
        <div className="flex items-center">
          <Building2 className="h-5 w-5 mr-2 shrink-0" />
          <span className="text-gray-700">
            {(() => {
              if (!career.institutions?.length) return "";
              // const institutions = [career.institutions, career.institutions]
              const institutions = career.institutions.map(
                (institution) => institution.name
              );
              const firstInstitution = institutions[0];
              const remainingInstitutions = institutions.slice(1);

              if (remainingInstitutions.length === 0) {
                return firstInstitution;
              }

              return (
                <Tooltip>
                  <TooltipTrigger className="text-gray-700">
                    {firstInstitution} + {remainingInstitutions.length} more
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="flex flex-col gap-1">
                      {remainingInstitutions.map((institution, index) => (
                        <span key={index}>{institution}</span>
                      ))}
                    </div>
                  </TooltipContent>
                </Tooltip>
              );
            })()}
          </span>
        </div>
      </CardContent>
      <CardFooter className="mt-auto pb-2" >
        <Link href={`/careers/${career.id}`} className="w-full">
          <Button
            variant="link"
            className="w-fit flex items-center justify-between group"
          >
            <span>Explore More</span>
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Button>
        </Link>
      </CardFooter>
    </Card>
  );
}
